const DEFAULT_SEEK_SECONDS = 3;
const DEFAULT_VOLUME_STEP = 0.05;
const DOUBLE_TAP_MS = 300;
const MAX_DOUBLE_TAP_DISTANCE = 30;

export function createPlayerInteractionController({
    videoPlayer,
    playerWrapper,
    feedback,

    seekSeconds = DEFAULT_SEEK_SECONDS,
    volumeStep = DEFAULT_VOLUME_STEP,

    isBlocked = () => false,

    onSizeShortcut = null
}) {
    let lastTapTime = 0;
    let lastTapX = null;
    let lastTapY = null;

    let feedbackTimeout = null;

    // -----------------------------------------
    // Utility
    // -----------------------------------------

    const getPlayer = () => {
        if (!videoPlayer) {
            return null;
        }

        // Streaming.vue
        // shallowRef<Player>
        if (videoPlayer.value !== undefined) {
            return videoPlayer.value;
        }

        // PipPlayer.vue
        // Player 자체
        return videoPlayer;
    };

    const getWrapper = () => {
        return playerWrapper?.value ?? null;
    };

    const clamp = (value, min, max) => {
        return Math.max(min, Math.min(max, value));
    };

    // -----------------------------------------
    // Feedback
    // -----------------------------------------

    const showFeedback = (direction, text) => {
        if (!feedback?.value) {
            return;
        }

        feedback.value.direction = direction;
        feedback.value.text = text;
        feedback.value.show = true;

        if (feedbackTimeout) {
            clearTimeout(feedbackTimeout);
        }

        feedbackTimeout = setTimeout(() => {
            if (feedback?.value) {
                feedback.value.show = false;
            }

            feedbackTimeout = null;
        }, 700);
    };

    // -----------------------------------------
    // Keyboard
    // -----------------------------------------

    const handleKeyDown = (event) => {
        const player = getPlayer();

        if (!player) {
            return;
        }

        if (document.activeElement?.tagName === 'TEXTAREA' || document.activeElement?.tagName === 'INPUT') {
            return;
        }

        const sizeMap = {
            Digit1: 25,
            Digit2: 40,
            Digit3: 55,
            Digit4: 75,
            Digit5: 90
        };

        if (sizeMap[event.code] !== undefined) {
            if (isBlocked()) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const percent = sizeMap[event.code];

            if (typeof onSizeShortcut === "function") {
                onSizeShortcut(percent);
            }

            return;
        }

        switch (event.code) {
            case 'Space': {
                event.preventDefault();

                if (player.paused()) {
                    player.play();
                } else {
                    player.pause();
                }
                break;
            }
            case 'ArrowRight': {
                if (document.querySelector('.zipImageViewer')) {
                    return;
                }

                const current = player.currentTime();
                player.currentTime(current + seekSeconds);
                break;
            }
            case 'ArrowLeft': {
                if (document.querySelector('.zipImageViewer')) {
                    return;
                }

                const current = player.currentTime();
                player.currentTime(current - seekSeconds);
                break;
            }
            case 'ArrowUp': {
                event.preventDefault();

                const currentVolume = player.volume();
                player.volume(Math.min(currentVolume + volumeStep, 1));
                break;
            }
            case 'ArrowDown': {
                event.preventDefault();

                const currentVolume = player.volume();
                player.volume(Math.max(currentVolume - volumeStep, 0));
                break;
            }
        }
    };

    // -----------------------------------------
    // Double click / double tap
    // -----------------------------------------

    const handleDoubleAction = (clientX) => {
        const player = getPlayer();
        const wrapper = getWrapper();
        if (!player || !wrapper) {
            return;
        }

        if (isBlocked()) {
            return;
        }

        const rect = wrapper.getBoundingClientRect();
        const relativeX = clientX - rect.left;
        const isRight = relativeX > rect.width / 2;
        const current = player.currentTime();
        const duration = player.duration() || 0;

        if (isRight) {
            const next = clamp(current + seekSeconds, 0, duration);
            player.currentTime(next);
            showFeedback('right', `+${seekSeconds}s`);
        } else {
            const prev = clamp(current - seekSeconds, 0, duration);
            player.currentTime(prev);
            showFeedback('left', `-${seekSeconds}s`);
        }

        try {
            const result = player.play();

            if (result?.catch) {
                result.catch(() => { });
            }
        } catch {
            // autoplay 등으로 play 실패해도 무시
        }
    };

    const onDblClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        handleDoubleAction(event.clientX);
    };

    const onTouchEnd = (event) => {
        const touch = event.changedTouches?.[0];

        if (!touch) {
            return;
        }

        const now = Date.now();

        const dx = lastTapX == null ? Infinity : Math.abs(touch.clientX - lastTapX);
        const dy = lastTapY == null ? Infinity : Math.abs(touch.clientY - lastTapY);
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (now - lastTapTime <= DOUBLE_TAP_MS && distance <= MAX_DOUBLE_TAP_DISTANCE) {
            handleDoubleAction(touch.clientX);

            lastTapTime = 0;
            lastTapX = null;
            lastTapY = null;

            return;
        }

        lastTapTime = now;
        lastTapX = touch.clientX;
        lastTapY = touch.clientY;
    };

    // -----------------------------------------
    // Wheel volume
    // -----------------------------------------

    const onWheelVolume = (event) => {
        const target = event.target;

        if (target?.closest?.('.playlist-overlay')) {
            return;
        }

        const player = getPlayer();

        if (!player) {
            return;
        }

        event.preventDefault();

        const delta = event.deltaY ?? event.wheelDelta ?? 0;

        let volume = player.volume();

        if (delta < 0) {
            volume = Math.min(1, volume + volumeStep);
        } else {
            volume = Math.max(0, volume - volumeStep);
        }

        player.volume(volume);
    };

    // -----------------------------------------
    // Play / Pause
    // -----------------------------------------

    const togglePlayPause = () => {
        const player = getPlayer();

        if (!player) {
            return;
        }

        if (player.paused()) {
            const result = player.play();

            if (result?.catch) {
                result.catch(() => { });
            }
        } else {
            player.pause();
        }
    };

    // -----------------------------------------
    // Cleanup
    // -----------------------------------------

    const init = () => {
        const wrapper = playerWrapper?.value;

        if (!wrapper) {
            return;
        }

        window.addEventListener('keydown', handleKeyDown);

        const player = getPlayer();
        if (player) {
            const playerEl = player.el();

            if (playerEl) {
                playerEl.addEventListener('wheel', onWheelVolume, { passive: false });
                playerEl.addEventListener(
                    'dblclick',
                    onDblClick
                );

                playerEl.addEventListener(
                    'wheel',
                    onWheelVolume,
                    { passive: false }
                );
            }
        }
    };


    const destroy = () => {

        window.removeEventListener('keydown', handleKeyDown);

        const player = getPlayer();
        if (player) {
            const playerEl = player.el();

            if (playerEl) {
                playerEl.removeEventListener('wheel', onWheelVolume);
                playerEl.removeEventListener('dblclick', onDblClick);
                playerEl.removeEventListener('touchend', onTouchEnd);
            }
        }

        if (feedbackTimeout) {
            clearTimeout(feedbackTimeout);
            feedbackTimeout = null;
        }

        lastTapTime = 0;
        lastTapX = null;
        lastTapY = null;
    };

    return {
        init,
        destroy,

        handleKeyDown,

        showFeedback,

        handleDoubleAction,
        onDblClick,
        onTouchEnd,

        onWheelVolume,

        togglePlayPause,
    };
}